import { useMemo, useState, useEffect, useRef } from 'react';
import { Box, Modal, Slide, Button, Typography, IconButton } from '@mui/material';
import { ReactComponent as LockIcon } from 'assets/LockIcon.svg';
import { ReactComponent as BackArrow } from 'assets/BackArrow.svg';
import { SidesheetRoutes, useSidesheet } from 'app/shared-components/sidesheets/SidesheetProvider';
import useSidesheetById from 'app/hooks/useSidesheetById';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectCardsDataSlice } from 'app/pages/store/CardDataSlice/cardDataSlice';
import { suspendPaymentCard } from 'app/pages/store/CardDataSlice/cardsThunks';
import { ReactComponent as SpeedchainLogoMobile } from 'assets/speedchain-logo-mobile.svg';
import { ReactComponent as MasterCardLogo } from 'assets/master-card-logo-mobile.svg';
import { ReactComponent as CardLockIcon } from 'assets/card-lock-icon.svg';
import { ReactComponent as LogoShape } from 'assets/LogoShape.svg';
import { getMobileCardDesign } from 'app/pages/mobile/utils/cardDesignUtils';
import { useNativeStatusBarStyle } from 'app/pages/mobile/hooks/useNativeStatusBarStyle';
import { selectReviewOnboardSlice } from 'app/pages/store/reviewOnboardSlice';
import { selectUserProfile } from 'app/store/userProfileSlice';
import { getAdmins, selectUsersSlice } from 'app/pages/store/usersSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import LockCardModal from './LockCardModal';
import LockCardSuccessModal from './LockCardSuccessModal';
import UnlockCardInfoModal from './UnlockCardInfoModal';

export interface MobileCardSidesheetParams {
  paymentCardId: string;
  onClose?: () => void;
}

function MobileCardSidesheet(): JSX.Element {
  const dispatch = useAppDispatch();
  const sidesheet = useSidesheetById(SidesheetRoutes.MobileCard);
  useNativeStatusBarStyle('light', sidesheet.isOpen);
  const { closeSidesheet } = useSidesheet();
  const params: MobileCardSidesheetParams = sidesheet.params;

  const { userCardList } = useAppSelector(selectCardsDataSlice);
  const { integration } = useAppSelector(selectReviewOnboardSlice);
  const { profile } = useAppSelector(selectUserProfile);
  const { selectedPaidolId } = useAppSelector(selectUserCompanies);
  const { groupAdmins } = useAppSelector(selectUsersSlice);

  const [isLockModalOpen, setIsLockModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isUnlockInfoModalOpen, setIsUnlockInfoModalOpen] = useState(false);
  const [lastAction, setLastAction] = useState<'lock' | 'unlock'>('lock');
  const [lockError, setLockError] = useState<string | null>(null);
  const [isLockLoading, setIsLockLoading] = useState(false);

  // Track the last fetched company to prevent stale admin data
  const lastFetchedPaidolId = useRef<string | null>(null);

  // Fetch admins when component mounts or when company changes
  useEffect(() => {
    if (selectedPaidolId && lastFetchedPaidolId.current !== selectedPaidolId) {
      const fetchAdmins = async () => {
        // Set immediately to prevent duplicate dispatches while request is pending
        const previousPaidolId = lastFetchedPaidolId.current;
        lastFetchedPaidolId.current = selectedPaidolId;

        try {
          await dispatch(getAdmins({ selectedCompany: selectedPaidolId, roleType: 'admin' })).unwrap();
          // Successfully fetched - keep the current value
        } catch (error) {
          // Reset on failure to allow retries
          lastFetchedPaidolId.current = previousPaidolId;
          // Error is handled by the Redux thunk
        }
      };
      fetchAdmins();
    }
  }, [dispatch, selectedPaidolId]);

  // Find the specific card
  const card = useMemo(() => {
    return userCardList?.find((c) => c.paymentCardId === params?.paymentCardId);
  }, [userCardList, params?.paymentCardId]);

  // Get card design
  const cardDesign = useMemo(() => {
    if (!card) return null;
    return getMobileCardDesign(integration?.highnoteProduct?.type, card.formFactor);
  }, [card, integration?.highnoteProduct?.type]);

  // Calculate card data
  const cardData = useMemo(() => {
    if (!card) return null;

    const spentValue = card.spent?.value ?? 0;
    const budgetValue = card.monthlySpendLimit?.amount;
    const isUnlimited = budgetValue == null;

    let availableAmount: number;
    let label: string;
    let progressPercentage: number;

    if (isUnlimited) {
      availableAmount = spentValue;
      label = 'Spent This Month';
      progressPercentage = 100;
    } else {
      availableAmount = budgetValue - spentValue;
      label = 'Available';
      progressPercentage =
        budgetValue > 0 ? Math.max(0, ((budgetValue - spentValue) / budgetValue) * 100) : 0;
    }

    const displayedAmountCents = isUnlimited ? spentValue : Math.max(availableAmount, 0);
    const availableFunds = `$${(displayedAmountCents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

    const spendingLimit = isUnlimited
      ? 'Unlimited'
      : `$${Math.abs(budgetValue / 100).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

    return { availableFunds, spendingLimit, label, progressPercentage, isUnlimited };
  }, [card]);

  // Get oldest admin's email
  const oldestAdminEmail = useMemo(() => {
    if (
      !selectedPaidolId ||
      lastFetchedPaidolId.current !== selectedPaidolId ||
      !groupAdmins ||
      groupAdmins.length === 0
    ) {
      return null;
    }

    // Filter out admins with missing or invalid createdAt
    const validAdmins = groupAdmins.filter((admin) => {
      if (!admin.createdAt) return false;
      const timestamp = new Date(admin.createdAt).getTime();
      return isFinite(timestamp) && !isNaN(timestamp);
    });

    // If no admins have valid timestamps, return null
    if (validAdmins.length === 0) return null;

    // Sort admins with valid timestamps by createdAt (oldest first)
    const sortedAdmins = [...validAdmins].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB;
    });

    return sortedAdmins[0]?.email || null;
  }, [groupAdmins, selectedPaidolId]);

  // Parse user address
  const userAddress = useMemo(() => {
    if (!profile?.address) return 'Address not available';

    try {
      // Safely parse the address JSON
      const addressStr =
        typeof profile.address === 'string' ? profile.address : JSON.stringify(profile.address);
      const address = JSON.parse(addressStr.replace(/\\/g, ''));

      // Validate required fields
      if (!address || typeof address !== 'object') {
        return 'Address not available';
      }

      const parts = [
        address.streetAddress,
        address.extendedAddress,
        address.locality,
        address.region && address.postalCode
          ? `${address.region}, ${address.postalCode}`
          : address.region || address.postalCode,
      ].filter(Boolean);

      return parts.length > 0 ? parts.join(' ') : 'Address not available';
    } catch {
      return 'Address not available';
    }
  }, [profile?.address]);

  const handleClose = () => {
    params?.onClose?.();
    closeSidesheet(SidesheetRoutes.MobileCard);
  };

  const handleLockCardClick = () => {
    const isLocked = card?.status && ['LOCKED', 'SUSPENDED'].includes(card.status);
    if (isLocked) {
      // If card is locked, show info modal instead of unlocking
      setIsUnlockInfoModalOpen(true);
    } else {
      // If card is not locked, show confirmation modal
      setIsLockModalOpen(true);
    }
  };

  const handleLockModalClose = () => {
    setIsLockModalOpen(false);
    setLockError(null);
  };

  const handleLockConfirm = async () => {
    if (!card?.paymentCardId) return;

    setIsLockLoading(true);
    setLockError(null);

    try {
      await dispatch(
        suspendPaymentCard({
          paymentCardId: card.paymentCardId,
        })
      ).unwrap();

      // Only close modal and show success if the operation succeeded
      setIsLockModalOpen(false);
      setLastAction('lock');
      setIsSuccessModalOpen(true);
    } catch (error) {
      // Handle the error and keep the modal open
      const errorMessage = error instanceof Error ? error.message : 'Failed to lock card. Please try again.';
      setLockError(errorMessage);
      // Error is handled by displaying it to the user
    } finally {
      setIsLockLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
    // Don't close the sidesheet - just return to card details
  };

  const handleUnlockInfoModalClose = () => {
    setIsUnlockInfoModalOpen(false);
  };

  return (
    <Modal open={sidesheet.isOpen} onClose={handleClose}>
      <Slide direction="up" in={sidesheet.isOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            paddingTop: 'var(--native-safe-top, env(safe-area-inset-top, 0px))',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            containerType: 'inline-size',
          }}
        >
          {/* Header gradient background */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '57vh',
              background: 'linear-gradient(131deg, #030B1B 41.65%, #2D3F64 88.19%)',
            }}
          />

          {/* Background logo */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -400,
              opacity: 1.0,

              mixBlendMode: 'overlay',
            }}
          >
            <LogoShape style={{ width: 1000, height: 1000 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              position: 'absolute',
              top: '10vh',
              left: 0,
              right: 0,
              transform: 'translateY(-50%)',
              textAlign: 'center',
              color: 'white',
              fontWeight: 600,
              fontSize: 'clamp(28px, 7vw, 36px)',
              letterSpacing: '-0.5px',
            }}
          >
            Card Details
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              top: '10vh',
              left: '3.5%',
              transform: 'translateY(-50%)',
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <BackArrow style={{ width: 20, height: 20 }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: '19vh',
              left: 0,
              right: 0,
              transform: 'translateY(-50%)',
              textAlign: 'center',
              color: 'white',
            }}
          >
            {!cardData?.isUnlimited && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: -0.5,
                    fontSize: 'clamp(18px, 4.5vw, 24px)',
                    letterSpacing: '-0.5px',
                    fontWeight: 400,
                  }}
                >
                  Available Funds
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    mb: 0.75,
                    fontSize: 'clamp(20px, 4.5vw, 28px)',
                  }}
                >
                  {cardData?.availableFunds || '$0.00'}
                </Typography>
              </>
            )}
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: 'clamp(12px, 3vw, 18px)',
                ...(cardData?.isUnlimited && {
                  fontSize: 'clamp(20px, 4.5vw, 28px)',
                  fontWeight: 600,
                  color: 'white',
                }),
              }}
            >
              Spending Limit: {cardData?.spendingLimit || '$0.00'}
            </Typography>
          </Box>

          {/* Card Display */}
          {cardDesign && cardData && (
            <Box
              sx={{
                position: 'absolute',
                top: '27vh',
                left: '7%',
                right: '7%',
                // Width is controlled by left/right positioning (7% from each side)
                height: 'clamp(185px, 24vh, 235px)', // Same responsive height as cards
                borderRadius: 4,
                border: '1px solid rgba(255, 255, 255, 0.5)',
                backgroundImage: `url(${cardDesign.cardImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: (theme) => theme.shadows[3],
                p: '6%', // Card internal spacing - 6% from each side
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',

                containerType: 'inline-size',
              }}
            >
              {/* Lock Icon Overlay */}
              {card?.status && ['LOCKED', 'SUSPENDED'].includes(card.status) && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'inherit',
                    backdropFilter: 'blur(3px)',
                  }}
                >
                  <CardLockIcon />
                </Box>
              )}
              {/* Card Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 'clamp(7px, 1.9vw, 15px)' }}>
                  <SpeedchainLogoMobile style={{ height: 'clamp(24px, 6vw, 40px)', width: 'auto' }} />
                  <Typography
                    variant="large"
                    sx={{ color: 'common.white', fontSize: 'clamp(14px, 5.5cqw, 18px)' }}
                  >
                    {card?.name || 'Field Card'}
                  </Typography>
                </Box>
                <Typography
                  variant="small"
                  sx={{
                    color: 'secondary.main',
                    fontWeight: 600,
                    fontSize: 'clamp(12px, 4.5cqw, 16px)',
                  }}
                >
                  Full Details
                </Typography>
              </Box>

              {/* Card Number */}
              <Typography
                variant="h5"
                sx={{
                  color: cardDesign.textColor === 'light' ? 'white' : 'text.secondary',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  letterSpacing: '-1px',
                  opacity: 0.9,
                  fontSize: 'clamp(16px, 6.5cqw, 22px)',
                  mt: 'clamp(4px, 0.9vh, 7px)',
                }}
              >
                {card?.last4 ? `* ${card.last4}` : `* 2345`}
              </Typography>

              {/* Amount and Progress Bar */}
              <Box sx={{ mt: 'clamp(15px, 3.8vh, 30px)' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'baseline',
                    gap: 'clamp(7px, 1.9vw, 15px)',
                    mb: 'clamp(4px, 0.9vh, 7px)',
                  }}
                >
                  <Typography
                    variant="large"
                    sx={{
                      color: cardDesign.textColor === 'light' ? 'white' : 'text.secondary',
                      opacity: 0.6,
                      fontSize: 'clamp(12px, 4.5cqw, 16px)',
                    }}
                  >
                    {cardData.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: cardDesign.textColor === 'light' ? 'white' : 'text.secondary',
                      textTransform: 'lowercase',
                      fontWeight: 400,
                      fontSize: 'clamp(14px, 5.5cqw, 18px)',
                    }}
                  >
                    {cardData.availableFunds}
                  </Typography>
                </Box>

                {/* Progress Bar */}
                <Box
                  sx={{
                    height: (theme) => theme.spacing(0.75),
                    borderRadius: (theme) => theme.spacing(1.5),
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(100, Math.max(0, cardData.progressPercentage))}%`,
                      height: '100%',
                      background: (theme) =>
                        `linear-gradient(90deg, ${theme.palette.primary.darker} 0%, ${theme.palette.secondary.main} 100%)`,
                      borderRadius: (theme) => theme.spacing(1.5),
                    }}
                  />
                </Box>
              </Box>

              {/* Card Footer */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 'clamp(7px, 1.9vw, 15px)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 'clamp(7px, 1.9vw, 15px)' }}>
                  <Typography
                    variant="medium"
                    sx={{
                      color: 'common.white',
                      opacity: 0.6,
                      fontSize: 'clamp(12px, 4.5cqw, 16px)',
                    }}
                  >
                    Exp
                  </Typography>
                  <Typography
                    variant="medium"
                    sx={{
                      color: 'common.white',
                      fontWeight: 500,
                      fontSize: 'clamp(12px, 4.5cqw, 16px)',
                    }}
                  >
                    {card?.expirationDate
                      ? new Date(card.expirationDate).toLocaleDateString('en-US', {
                          month: '2-digit',
                          year: '2-digit',
                        })
                      : '2/26'}
                  </Typography>
                </Box>
                <MasterCardLogo style={{ height: 'clamp(24px, 6vw, 40px)', width: 'auto' }} />
              </Box>
            </Box>
          )}

          {/* Content box starting at orange line */}
          <Box
            sx={{
              position: 'absolute',
              top: '60vh',
              left: '3.5%',
              right: '3.5%',
              bgcolor: '#F2F6FF',
              height: '75px',
              borderRadius: 2,

              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Billing Address
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.primary' }}>
              {userAddress}
            </Typography>
          </Box>
          <Box
            onClick={handleLockCardClick}
            sx={{
              position: 'absolute',
              top: '76vh',
              left: '3.5%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,

              cursor: 'pointer',
              '&:hover': {
                opacity: 0.7,
              },
            }}
          >
            <LockIcon style={{ color: '#324C80', width: 24, height: 25 }} />
            <Typography
              variant="body1"
              sx={{ color: 'text.primary', fontSize: 'clamp(16px, 4vw, 20px)', fontWeight: 400 }}
            >
              {card?.status && ['LOCKED', 'SUSPENDED'].includes(card.status) ? 'Unlock card' : 'Lock card'}
            </Typography>
          </Box>

          {/* Back button box */}
          <Box
            sx={{
              position: 'absolute',
              top: '87vh',
              left: 0,
              right: 0,
              px: 3,
              py: 3,
              bgcolor: 'background.default',
            }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={handleClose}
              sx={{
                py: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.125rem',
                fontWeight: 600,
              }}
            >
              Back
            </Button>
          </Box>

          {/* Lock Card Modal */}
          {isLockModalOpen && (
            <LockCardModal
              onClose={handleLockModalClose}
              onConfirm={handleLockConfirm}
              cardLast4={card?.last4}
              error={lockError}
              isLoading={isLockLoading}
            />
          )}

          {/* Success Modal */}
          {isSuccessModalOpen && (
            <LockCardSuccessModal onClose={handleSuccessModalClose} action={lastAction} />
          )}

          {isUnlockInfoModalOpen && (
            <UnlockCardInfoModal onClose={handleUnlockInfoModalClose} adminEmail={oldestAdminEmail} />
          )}
        </Box>
      </Slide>
    </Modal>
  );
}

export default MobileCardSidesheet;
